/**
 * Determines the API URL based on environment and hostname.
 * Priority:
 * 1. DEV mode -> localhost
 * 2. Explicit VITE_API_URL env var (if set and not production default)
 * 3. Hostname-based detection (staging.getvitly.com -> staging API)
 * 4. Production fallback
 */
const getApiUrl = (): string => {
  // Development mode always uses localhost
  if (import.meta.env.DEV) {
    return 'http://localhost:8281';
  }

  // Check for explicit env var first
  const explicitUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (explicitUrl && explicitUrl.trim() !== '' && explicitUrl.includes('staging')) {
    return explicitUrl;
  }

  // Auto-detect staging based on hostname (works even without correct env vars)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Staging environments
    if (hostname === 'staging.getvitly.com' ||
        hostname.includes('staging') ||
        hostname.endsWith('-staging.vercel.app')) {
      console.info('[env] Detected staging environment via hostname:', hostname);
      return 'https://staging.api.getvitly.com';
    }

    // Vercel preview deployments (non-staging) - use production by default
    // These typically have format: project-hash-org.vercel.app
    if (hostname.endsWith('.vercel.app')) {
      console.info('[env] Detected Vercel preview, using production API');
      return 'https://api.getvitly.com';
    }
  }

  // Production fallback (getvitly.com or any other domain)
  return explicitUrl?.trim() || 'https://api.getvitly.com';
};

const requireEnv = (key: string): string => {
  // Special handling for API_URL with hostname-based detection
  if (key === 'VITE_API_URL') {
    return getApiUrl();
  }

  const rawValue = import.meta.env[key as keyof ImportMetaEnv] as string | undefined;

  if (typeof rawValue === 'string' && rawValue.trim() !== '') {
    return rawValue;
  }

  throw new Error(`[env] Missing required environment variable: ${key}`);
};

export const API_URL = requireEnv('VITE_API_URL');

/**
 * Determines the WebSocket URL based on environment and hostname.
 * Priority:
 * 1. DEV mode -> localhost
 * 2. Explicit VITE_WS_URL env var (if set and valid)
 * 3. Hostname-based detection (staging.getvitly.com -> staging API)
 * 4. Derive from API_URL
 */
const getWsUrl = (): string => {
  // Development mode always uses localhost
  if (import.meta.env.DEV) {
    return 'http://localhost:8281';
  }

  // Check for explicit env var
  const explicitWsUrl = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicitWsUrl && explicitWsUrl.trim() !== '' && explicitWsUrl.includes('staging')) {
    return explicitWsUrl;
  }

  // Auto-detect staging based on hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Staging environments
    if (hostname === 'staging.getvitly.com' ||
        hostname.includes('staging') ||
        hostname.endsWith('-staging.vercel.app')) {
      console.info('[env] WebSocket using staging API based on hostname:', hostname);
      return 'https://staging.api.getvitly.com';
    }
  }

  // Use explicit WS URL or derive from API URL
  return explicitWsUrl?.trim() || API_URL;
};

export const WS_URL = getWsUrl();
